/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { Channel, CHANNELS } from 'cio/utils/newsletter-channel-info';

import NewsletterModel, { ChannelType } from 'cio/models/newsletter';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import AuthService from 'cio/services/auth';

interface ChannelObject {
  type: ChannelType;
}

interface ChannelListArgs {
  newsletter: NewsletterModel;
  onSelect: (channelType: ChannelType) => unknown;
}

export default class ChannelList extends Component<ChannelListArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;

  @tracked pendingChannel?: ChannelObject;

  get channelIsWebhook() {
    return this.auth.redacted && this.args.newsletter.channel !== 'webhook';
  }

  get channels() {
    return _sortBy(
      CHANNELS.filter((channel: Channel) => this.doesNotRequireFeatureFlagOrIsEnabled(channel))
        .map((channel: Channel) => this.#addChannelStateInfo(channel))
        .map((channel: Channel) => this.#disableForRedactedUsers(channel)),
      ['type'],
    );
  }

  #disableForRedactedUsers(channel: Channel) {
    if (this.auth.redacted && channel.type === 'webhook') {
      return {
        ...channel,
        enabled: !this.channelIsWebhook,
      };
    }
    return channel;
  }

  private doesNotRequireFeatureFlagOrIsEnabled(channel: Channel) {
    let { featureFlag } = channel.info;
    return featureFlag == null || this.features.isEnabled(featureFlag);
  }

  #addChannelStateInfo(channel: Channel) {
    return {
      ...channel,
      enabled: this.environment.enabledAction(channel.type),
    };
  }

  get currentChannelHasContent() {
    let { newsletter } = this.args;
    return newsletter.activeTemplates.any(
      (template) => (template.hasContent as boolean) || (template.editor === 'json' && template.hasJsonContent),
    );
  }

  @action
  onCancelChannelChange() {
    this.pendingChannel = undefined;
    this.analytics.track('broadcast', 'edit_discarded', { model: this.args.newsletter, from: 'content' });
  }

  @action
  onConfirmChannelChange(channelType: ChannelType) {
    this.args.onSelect(channelType);
    this.pendingChannel = undefined;
  }

  @action
  onChannelChange(channel: { type: ChannelType }) {
    let { newsletter } = this.args;
    let hasContent = this.currentChannelHasContent;

    if (channel.type === newsletter.channel) {
      return false;
    }

    this.analytics.track('broadcast', 'edit_started', {
      model: this.args.newsletter,
      channelChangedTo: channel.type,
      editType: 'channel_changed',
    });

    if (!newsletter.channel || !hasContent) {
      return this.onConfirmChannelChange(channel.type);
    } else {
      this.pendingChannel = channel;
      return false;
    }
  }
}
